import { useQuery } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { SwiperSlide } from "swiper/react";
import { createEnumParam, useQueryParams, withDefault } from "use-query-params";

import ActionIconButton from "@components/buttons/ActionIconButton";
import Segment from "@components/buttons/Segment";
import Tab from "@components/buttons/Tab";
import TertiaryButton from "@components/buttons/TertiaryButton";
import styles from "@components/forms/careProfile/CareProfile.module.css";
import CareProfileCareAndHomeForm from "@components/forms/careProfile/CareProfileCareAndHomeForm";
import CareProfileCarePhaseForm from "@components/forms/careProfile/CareProfileCarePhaseForm";
import CareProfileDemographicsForm from "@components/forms/careProfile/CareProfileDemographicsForm";
import CareProfileFinancialForm from "@components/forms/careProfile/CareProfileFinancialForm";
import CareProfileHistory from "@components/forms/careProfile/CareProfileHistory";
import CareProfileInvitee from "@components/forms/careProfile/CareProfileInvitee";
import CareProfileLegalForm from "@components/forms/careProfile/CareProfileLegalForm";
import CareProfileMedicalForm from "@components/forms/careProfile/CareProfileMedicalForm";
import CareProfileMemberForm from "@components/forms/careProfile/CareProfileMemberForm";
import CareProfileRiskForm from "@components/forms/careProfile/CareProfileRiskForm";
import CareProfileSection from "@components/forms/careProfile/CareProfileSection";
import CareProfileSocialForm from "@components/forms/careProfile/CareProfileSocialForm";
import CareProfileUseCaseForm from "@components/forms/careProfile/CareProfileUseCaseForm";
import ConsultReports from "@components/forms/careProfile/ConsultReports";
import InternalNotes from "@components/forms/careProfile/InternalNotes";
import InviteAccountMemberForm from "@components/forms/InviteAccountMemberForm";
import Modal from "@components/Modal";
import Slides from "@components/Slides";
import BodyCopy from "@components/typography/BodyCopy";
import Heading1 from "@components/typography/Heading1";
import useOpener from "@hooks/useOpener";
import {
  ClientRoleRoleEnum,
  GetCareProfileFieldRulesDocument,
  GetCareProfileFieldRulesQuery,
  GetCareProfileFieldRulesQueryVariables,
  GetJourneyWithCareProfileQuery,
  InternalNoteKindEnum
} from "@typing/Generated";
import { getCareProfileName } from "@utils/modelUtils";

type Props = {
  journey: GetJourneyWithCareProfileQuery["journey"];
};

enum ModeEnum {
  HISTORY = "history",
  PROFILE = "profile"
}

enum DocumentationSectionEnum {
  NOTES = "notes",
  PLAN = "plan",
  SESSIONS = "sessions",
  SYNOPSIS = "synopsis"
}

enum CareProfileDataTypeEnum {
  CARE_TEAM_INVITE = "careTeamInvite",
  CLIENT_ROLE = "clientRole",
  LOVED_ONE = "lovedOne"
}

type CareProfileDataSelection = {
  careTeamInviteId?: string | null;
  clientRoleId?: string | null;
  dataType: CareProfileDataTypeEnum;
};

const CareProfile = ({ journey }: Props) => {
  const documentationSectionParam = createEnumParam<DocumentationSectionEnum>(Object.values(DocumentationSectionEnum));
  const modeParam = createEnumParam<ModeEnum>(Object.values(ModeEnum));

  const [careProfileDataSelection, setCareProfileDataSelection] = useState<CareProfileDataSelection>({
    dataType: CareProfileDataTypeEnum.LOVED_ONE
  });

  const intl = useIntl();

  const useCaseOpener = useOpener();
  const inviteOpener = useOpener();

  const [query, setQuery] = useQueryParams({
    documentationSection: withDefault(
      { ...documentationSectionParam, urlName: "docSection" },
      DocumentationSectionEnum.SYNOPSIS
    ),
    mode: withDefault({ ...modeParam, urlName: "careProfileMode" }, ModeEnum.PROFILE)
  });

  const getCareProfileRulesQuery = useQuery<GetCareProfileFieldRulesQuery, GetCareProfileFieldRulesQueryVariables>(
    GetCareProfileFieldRulesDocument
  );

  const fieldRules = getCareProfileRulesQuery.data?.careProfileFieldRules ?? [];

  const modeButtons = [
    {
      maxWidth: 180,
      textKey: "pages.careProfile.modes.profile",
      value: ModeEnum.PROFILE
    },
    {
      maxWidth: 180,
      textKey: "pages.careProfile.modes.history",
      value: ModeEnum.HISTORY
    }
  ];

  const [documentationSection, setDocumentationSection] = useState<DocumentationSectionEnum>(
    query.documentationSection
  );
  const [mode, setMode] = useState<ModeEnum>(query.mode);

  useEffect(() => {
    setQuery({ documentationSection, mode });
  }, [documentationSection, mode, setQuery]);

  const handleInviteCanceled = useCallback(
    (inviteId: string | null | undefined) => {
      if (careProfileDataSelection.dataType === CareProfileDataTypeEnum.CARE_TEAM_INVITE) {
        if (careProfileDataSelection.careTeamInviteId === inviteId) {
          setCareProfileDataSelection({ dataType: CareProfileDataTypeEnum.LOVED_ONE });
        }
      }
    },
    [careProfileDataSelection.careTeamInviteId, careProfileDataSelection.dataType]
  );

  const careProfileName = getCareProfileName({ intl, journey });

  return (
    <div>
      <div className={styles.headingRow}>
        <Heading1>{careProfileName}</Heading1>
        <div className={styles.modeSegmentBlock}>
          <Segment
            buttons={modeButtons}
            onChange={v => {
              setMode(v);
            }}
            value={mode}
          />
        </div>
      </div>

      {mode === ModeEnum.PROFILE && (
        <>
          <div className={styles.documentationSection}>
            <div className={styles.documentationSectionTabs}>
              <div className={styles.tabsRow}>
                <Tab
                  active={documentationSection === DocumentationSectionEnum.SYNOPSIS}
                  onClick={() => setDocumentationSection(DocumentationSectionEnum.SYNOPSIS)}
                  textKey="pages.careProfile.documentationSections.synopsis"
                />
                <Tab
                  active={documentationSection === DocumentationSectionEnum.PLAN}
                  onClick={() => setDocumentationSection(DocumentationSectionEnum.PLAN)}
                  textKey="pages.careProfile.documentationSections.plan"
                />
                <Tab
                  active={documentationSection === DocumentationSectionEnum.NOTES}
                  onClick={() => setDocumentationSection(DocumentationSectionEnum.NOTES)}
                  textKey="pages.careProfile.documentationSections.notes"
                />
                <Tab
                  active={documentationSection === DocumentationSectionEnum.SESSIONS}
                  onClick={() => setDocumentationSection(DocumentationSectionEnum.SESSIONS)}
                  textKey="pages.careProfile.documentationSections.sessions"
                />
              </div>
            </div>

            {documentationSection === DocumentationSectionEnum.NOTES && (
              <div>
                <InternalNotes journey={journey} noteKind={InternalNoteKindEnum.NOTE} />
              </div>
            )}
            {documentationSection === DocumentationSectionEnum.PLAN && (
              <div>
                <InternalNotes journey={journey} noteKind={InternalNoteKindEnum.ROADMAP} />
              </div>
            )}
            {documentationSection === DocumentationSectionEnum.SESSIONS && <ConsultReports journey={journey} />}
            {documentationSection === DocumentationSectionEnum.SYNOPSIS && (
              <div>
                <InternalNotes journey={journey} noteKind={InternalNoteKindEnum.SYNOPSIS} />
              </div>
            )}
          </div>

          <div>
            <div className={styles.spreadRowLeft}>
              <div className={styles.slides}>
                <Slides
                  overrideNavigationLeft="-20px"
                  overrideNavigationTop="15%"
                  slidesPerView="auto"
                  spaceBetween={5}
                >
                  <SwiperSlide className={styles.slide}>
                    <Tab
                      active={careProfileDataSelection.dataType === CareProfileDataTypeEnum.LOVED_ONE}
                      icon="handHoldingHeart"
                      onClick={() => setCareProfileDataSelection({ dataType: CareProfileDataTypeEnum.LOVED_ONE })}
                      text={careProfileName}
                    />
                  </SwiperSlide>

                  {journey.clientRoles.map(clientRole => (
                    <SwiperSlide className={styles.slide} key={clientRole.id}>
                      <Tab
                        active={
                          careProfileDataSelection.dataType === CareProfileDataTypeEnum.CLIENT_ROLE &&
                          careProfileDataSelection.clientRoleId === clientRole.id
                        }
                        icon={clientRole.role === ClientRoleRoleEnum.OWNER ? "user" : null}
                        onClick={() =>
                          setCareProfileDataSelection({
                            clientRoleId: clientRole.id,
                            dataType: CareProfileDataTypeEnum.CLIENT_ROLE
                          })
                        }
                        text={clientRole.client.fullName ?? intl.formatMessage({ id: "dictionary.unknown" })}
                      />
                    </SwiperSlide>
                  ))}
                  {journey.careTeamInvites
                    ?.filter(invite => !invite.canceled && !invite.accepted)
                    ?.map(invite => (
                      <SwiperSlide className={styles.slide} key={invite.id}>
                        <Tab
                          active={
                            careProfileDataSelection.dataType === CareProfileDataTypeEnum.CARE_TEAM_INVITE &&
                            careProfileDataSelection.careTeamInviteId === invite.id
                          }
                          icon="hourglass"
                          onClick={() =>
                            setCareProfileDataSelection({
                              careTeamInviteId: invite.id,
                              dataType: CareProfileDataTypeEnum.CARE_TEAM_INVITE
                            })
                          }
                          text={invite.email ?? intl.formatMessage({ id: "dictionary.unknown" })}
                        />
                      </SwiperSlide>
                    ))}
                </Slides>
              </div>
              <div className={styles.inviteSection}>
                {inviteOpener.isOpen ? (
                  <InviteAccountMemberForm
                    journey={journey}
                    onCancel={() => inviteOpener.close()}
                    onSuccess={inviteOpener.close}
                  />
                ) : (
                  <TertiaryButton
                    icon="add"
                    onClick={() => {
                      inviteOpener.open();
                    }}
                    textKey="forms.careProfileNew.addMember"
                  />
                )}
              </div>
            </div>

            {careProfileDataSelection.dataType === CareProfileDataTypeEnum.LOVED_ONE ? (
              <div className={styles.lovedOneSections}>
                <CareProfileSection color="secondary-100" sectionName="useCaseAndRisk">
                  <div className={styles.useCaseAndRisk}>
                    <div className={styles.useCaseControl}>
                      {journey.careProfile.useCase ? (
                        <BodyCopy>
                          <FormattedMessage id={`models.careProfile.useCaseOptions.${journey.careProfile.useCase}`} />
                        </BodyCopy>
                      ) : (
                        <BodyCopy>
                          <FormattedMessage id="models.careProfile.useCaseNotSet" />
                        </BodyCopy>
                      )}
                      <ActionIconButton
                        ariaLabelKey="accessibility.ariaLabels.edit"
                        icon="pencil"
                        onClick={useCaseOpener.open}
                      />
                    </div>
                    <CareProfileCarePhaseForm careProfileFieldRules={fieldRules} journey={journey} />
                    <CareProfileRiskForm careProfileFieldRules={fieldRules} journey={journey} />
                  </div>
                </CareProfileSection>
                <CareProfileSection color="mint" defaultOpen sectionName="demographics">
                  <CareProfileDemographicsForm careProfileFieldRules={fieldRules} journey={journey} />
                </CareProfileSection>

                <CareProfileSection color="primary-100" sectionName="medical">
                  <CareProfileMedicalForm careProfileFieldRules={fieldRules} journey={journey} />
                </CareProfileSection>

                <CareProfileSection color="light-yellow" sectionName="careAndHome">
                  <CareProfileCareAndHomeForm careProfileFieldRules={fieldRules} journey={journey} />
                </CareProfileSection>

                <CareProfileSection color="light-red" sectionName="financial">
                  <CareProfileFinancialForm careProfileFieldRules={fieldRules} journey={journey} />
                </CareProfileSection>

                <CareProfileSection color="primary-50" sectionName="social">
                  <CareProfileSocialForm careProfileFieldRules={fieldRules} journey={journey} />
                </CareProfileSection>

                <CareProfileSection color="light-blue" sectionName="legal">
                  <CareProfileLegalForm careProfileFieldRules={fieldRules} journey={journey} />
                </CareProfileSection>
              </div>
            ) : (
              <>
                {careProfileDataSelection.dataType === CareProfileDataTypeEnum.CLIENT_ROLE ? (
                  <div className={`${styles.formSection} ${styles.memberDataSection}`}>
                    <div>
                      <BodyCopy bold>
                        <FormattedMessage id="pages.careProfile.sectionTitles.memberData" />
                      </BodyCopy>
                    </div>
                    <CareProfileMemberForm
                      careProfileFieldRules={fieldRules}
                      clientRoleId={careProfileDataSelection.clientRoleId}
                      journey={journey}
                    />
                  </div>
                ) : (
                  <div className={`${styles.formSection} ${styles.memberDataSection}`}>
                    <div>
                      <BodyCopy bold>
                        <FormattedMessage id="pages.careProfile.sectionTitles.memberData" />
                      </BodyCopy>
                    </div>
                    <CareProfileInvitee
                      careTeamInviteId={careProfileDataSelection.careTeamInviteId}
                      journey={journey}
                      onCancel={handleInviteCanceled}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
      {mode === ModeEnum.HISTORY && <CareProfileHistory journey={journey} />}
      <Modal opener={useCaseOpener} title={intl.formatMessage({ id: "pages.careProfile.updateUseCase" })}>
        {({ modalDisplayed }) => (
          <CareProfileUseCaseForm
            autofocus={modalDisplayed}
            journey={journey}
            onCancel={useCaseOpener.close}
            onError={() => {}}
            onSuccess={useCaseOpener.close}
          />
        )}
      </Modal>
    </div>
  );
};

export default CareProfile;
